import { useMemo, useState } from 'react'
import { KpiCard, HorizontalBarChart, DonutChart, AreaLineChart, Alert, EmptyState, Input, Button } from '@/components/ui'
import { DollarSign, FileCheck2, Percent, Clock, Loader2, RefreshCw } from 'lucide-react'
import { fmtMoney, fmtNumber } from '@/lib/utils'
import { useKpisComprobantes, useHistoricoComprobantes } from '@/lib/api/comprobantes'

export function Kpis() {
  const hoy = new Date()
  const hace30 = new Date(hoy.getTime() - 30 * 86_400_000)
  const [desde, setDesde] = useState(hace30.toISOString().slice(0, 10))
  const [hasta, setHasta] = useState(hoy.toISOString().slice(0, 10))

  const { data: kpis = [], isLoading, error, refetch, isRefetching } = useKpisComprobantes({ desde, hasta })

  // Para gráficos de "top motivos de rechazo" + "por banco" usamos el histórico
  // (los KPIs del RPC no desglosan esos ejes).
  const { data: historico = [] } = useHistoricoComprobantes(500)

  // Totales agregados del período
  const totales = useMemo(() => {
    const totalProcesados = kpis.reduce((a, k) => a + k.total, 0)
    const totalAuto = kpis.reduce((a, k) => a + k.validados_auto, 0)
    const totalManual = kpis.reduce((a, k) => a + k.validados_manual, 0)
    const totalRechazados = kpis.reduce((a, k) => a + k.rechazados, 0)
    const totalValidado = kpis.reduce((a, k) => a + Number(k.monto_total_validado ?? 0), 0)
    const pctAuto = totalProcesados > 0 ? (totalAuto / totalProcesados) * 100 : 0

    // p50 ponderado: aprox. mediana de medianas
    const latencias = kpis.map(k => k.latencia_p50_ms).filter((v): v is number => v !== null && v > 0).sort((a, b) => a - b)
    const latP50 = latencias.length > 0 ? latencias[Math.floor(latencias.length / 2)] : null

    return { totalProcesados, totalAuto, totalManual, totalRechazados, totalValidado, pctAuto, latP50 }
  }, [kpis])

  // Series temporales (ordenadas ascendentes para el gráfico de área)
  const serieMontos = useMemo(() => {
    const asc = [...kpis].sort((a, b) => a.dia.localeCompare(b.dia))
    return asc.map(k => Number(k.monto_total_validado ?? 0))
  }, [kpis])

  // Donut Auto IA vs Humano vs Rechazados (del período)
  const donutSegments = useMemo(() => {
    const t = totales.totalAuto + totales.totalManual + totales.totalRechazados
    if (t === 0) return []
    return [
      { label: 'Auto IA', value: totales.totalAuto, color: '#10b981' },
      { label: 'Humano aprobó', value: totales.totalManual, color: '#f59e0b' },
      { label: 'Rechazados', value: totales.totalRechazados, color: '#ef4444' },
    ]
  }, [totales])

  // Top motivos rechazo (del histórico filtrado al período)
  const motivosRechazo = useMemo(() => {
    const desdeMs = new Date(desde + 'T00:00:00').getTime()
    const hastaMs = new Date(hasta + 'T23:59:59').getTime()
    const rechazados = historico.filter(c => {
      const t = new Date(c.creado_en).getTime()
      return c.estado === 'rechazado' && t >= desdeMs && t <= hastaMs
    })

    const counts: Record<string, number> = {}
    for (const r of rechazados) {
      const motivo = (r.match_razon ?? r.revision_nota ?? 'sin_motivo').trim().toLowerCase()
      // Agrupar por patrones comunes
      const key =
        motivo.includes('fecha') ? '📅 Fecha fuera del mes' :
        motivo.includes('beneficiario') || motivo.includes('point') ? '⛔ Beneficiario no es Point' :
        motivo.includes('cliente') || motivo.includes('vincul') ? '👤 Cliente no vinculado' :
        motivo.includes('monto') || motivo.includes('ilegible') ? '🔢 Monto ilegible' :
        motivo.includes('cuenta') ? '🏦 Cuenta destino inválida' :
        motivo.includes('duplic') ? '🔁 Duplicado' :
        '⚠ Otros'
      counts[key] = (counts[key] ?? 0) + 1
    }

    const total = rechazados.length
    const colors: Record<string, string> = {
      '📅 Fecha fuera del mes': '#f59e0b',
      '⛔ Beneficiario no es Point': '#ef4444',
      '👤 Cliente no vinculado': '#be123c',
      '🔢 Monto ilegible': '#d97706',
      '🏦 Cuenta destino inválida': '#9f1239',
      '🔁 Duplicado': '#7c2d12',
      '⚠ Otros': '#94a3b8',
    }
    return Object.entries(counts)
      .map(([label, value]) => ({ label, value, total, color: colors[label] ?? '#94a3b8' }))
      .sort((a, b) => b.value - a.value)
  }, [historico, desde, hasta])

  // Comprobantes por banco (del histórico filtrado al período, válidos)
  const bancos = useMemo(() => {
    const desdeMs = new Date(desde + 'T00:00:00').getTime()
    const hastaMs = new Date(hasta + 'T23:59:59').getTime()
    const validos = historico.filter(c => {
      const t = new Date(c.creado_en).getTime()
      return c.banco_extraido && t >= desdeMs && t <= hastaMs
    })

    const counts: Record<string, number> = {}
    for (const v of validos) {
      const b = v.banco_extraido!.trim()
      counts[b] = (counts[b] ?? 0) + 1
    }

    const total = validos.length
    const palette = ['#facc15', '#3b82f6', '#10b981', '#94a3b8', '#1e40af', '#f59e0b', '#ef4444', '#8b5cf6']
    return Object.entries(counts)
      .map(([label, value], i) => ({ label, value, total, color: palette[i % palette.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [historico, desde, hasta])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-slate-500">Cargando KPIs…</span>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger" title="Error al cargar KPIs">{(error as Error).message}</Alert>
  }

  const sinDatos = totales.totalProcesados === 0

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <Input label="Desde" type="date" value={desde} onChange={e => setDesde(e.target.value)} className="w-40" />
        <Input label="Hasta" type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="w-40" />
        <div className="ml-auto">
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={isRefetching ? 'w-3.5 h-3.5 animate-spin' : 'w-3.5 h-3.5'} /> Refrescar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Monto validado"
          value={fmtMoney(totales.totalValidado)}
          sub={`${diasRango(desde, hasta)} días`}
          icon={<DollarSign className="w-3.5 h-3.5" />}
          color="emerald"
        />
        <KpiCard
          label="Comprobantes procesados"
          value={fmtNumber(totales.totalProcesados)}
          sub={`${fmtNumber(totales.totalAuto + totales.totalManual)} validados`}
          icon={<FileCheck2 className="w-3.5 h-3.5" />}
        />
        <KpiCard
          label="Tasa auto IA"
          value={`${totales.pctAuto.toFixed(1)}%`}
          sub={`${fmtNumber(totales.totalAuto)} de ${fmtNumber(totales.totalProcesados)}`}
          icon={<Percent className="w-3.5 h-3.5" />}
          color={totales.pctAuto >= 70 ? 'emerald' : 'amber'}
        />
        <KpiCard
          label="Latencia IA p50"
          value={totales.latP50 ? `${(totales.latP50 / 1000).toFixed(1)}s` : '—'}
          sub="GPT-4.1 vision"
          icon={<Clock className="w-3.5 h-3.5" />}
        />
      </div>

      {sinDatos ? (
        <EmptyState
          title="Sin datos en el período"
          description="Ajustá el rango de fechas para ver KPIs."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <header className="mb-3">
              <h2 className="font-display text-lg text-navy-700">Monto validado por día</h2>
              <p className="text-xs text-slate-500">
                Promedio diario {fmtMoney(totales.totalValidado / Math.max(1, kpis.length))} USD
              </p>
            </header>
            {serieMontos.length > 1 ? (
              <AreaLineChart values={serieMontos} color="#10b981" height={200} />
            ) : (
              <div className="text-center text-slate-500 py-10 text-sm">
                Pocos datos para graficar
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <header className="mb-3">
              <h2 className="font-display text-lg text-navy-700">Auto IA vs Humano</h2>
              <p className="text-xs text-slate-500">De {fmtNumber(totales.totalProcesados)} comprobantes del período</p>
            </header>
            {donutSegments.length > 0 ? (
              <div className="flex items-center justify-center gap-5 flex-wrap">
                <DonutChart
                  size={180}
                  segments={donutSegments}
                  centerValue={`${totales.pctAuto.toFixed(0)}%`}
                  centerLabel="Auto IA"
                />
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-emerald-500" />
                    <span>Auto IA</span>
                    <span className="font-mono text-slate-500 ml-2">
                      {fmtNumber(totales.totalAuto)} ({totales.totalProcesados ? Math.round((totales.totalAuto / totales.totalProcesados) * 100) : 0}%)
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-amber-500" />
                    <span>Humano aprobó</span>
                    <span className="font-mono text-slate-500 ml-2">
                      {fmtNumber(totales.totalManual)} ({totales.totalProcesados ? Math.round((totales.totalManual / totales.totalProcesados) * 100) : 0}%)
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-rose-500" />
                    <span>Rechazados</span>
                    <span className="font-mono text-slate-500 ml-2">
                      {fmtNumber(totales.totalRechazados)} ({totales.totalProcesados ? Math.round((totales.totalRechazados / totales.totalProcesados) * 100) : 0}%)
                    </span>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="text-center text-slate-500 py-10 text-sm">Sin resueltos en el período</div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <header className="mb-3">
              <h2 className="font-display text-lg text-navy-700">Top motivos de rechazo</h2>
              <p className="text-xs text-slate-500">De {totales.totalRechazados} comprobantes rechazados</p>
            </header>
            {motivosRechazo.length > 0 ? (
              <HorizontalBarChart items={motivosRechazo} />
            ) : (
              <div className="text-center text-slate-500 py-10 text-sm">Sin rechazos en el período 🎉</div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <header className="mb-3">
              <h2 className="font-display text-lg text-navy-700">Comprobantes por banco origen</h2>
              <p className="text-xs text-slate-500">De {bancos.reduce((a, b) => a + b.value, 0)} con banco identificado</p>
            </header>
            {bancos.length > 0 ? (
              <HorizontalBarChart items={bancos} showPercent={false} />
            ) : (
              <div className="text-center text-slate-500 py-10 text-sm">Sin datos de bancos en el período</div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

function diasRango(desde: string, hasta: string): number {
  const d = new Date(desde).getTime()
  const h = new Date(hasta).getTime()
  return Math.max(1, Math.round((h - d) / 86_400_000) + 1)
}
